import { DialogContent, Box, Typography, Tooltip } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import AvatarComponent from '@hapana/shared/web/components/shared/avatar/avatar.component'
import { AVATAR_SIZE } from '@hapana/shared/web/components/shared/avatar/avatar.constants'
import DialogLayout from '@hapana/shared/web/components/shared/dialog/dialog-layout/dialog-layout.component'
import {
  CLOSE_TYPES,
  SIZES,
} from '@hapana/shared/web/components/shared/dialog/dialog-layout/dialog-layout.constants'
import { useDialog } from '@hapana/shared/web/components/shared/dialog/dialog.hooks'
import ExclamationTriangleIcon from '@hapana/shared/web/styles/assets/svgs/exclamation-triangle.svg'
import { useCreateAddonInvoice } from '_hooks/equipment-assignments.hooks'
import { useSiteEnableMembershipPlanChange } from '_hooks/sites.hooks'
import { ROUTES } from '../../../../../../app.constants'
import { useChangeMembershipPlan } from '_pages/clients/components/client-packages/hooks'
import { ChangePlanDialog } from '_pages/clients/components/client-packages/dialogs'

import { styles } from './add-on-confirmation-dialog.styles'

const ADDON_OPTION = {
  CHANGE_MEMBERSHIP: 'change_membership',
  ONE_TIME_ADDON: 'one_time_addon',
}

export const AddOnConfirmationDialog = ({
  addOnPrice,
  addOnName,
  sessionTypeName,
  clientName,
  clientProfileImage,
  addonConfigId: _addonConfigId,
  serviceId,
  serviceName,
  servicePrice,
  serviceCurrency,
  serviceNextBillingDate,
  siteId,
  clientId,
  equipmentId,
  siteSessionTypeId,
  showOneTimeAddon = true,
}) => {
  const { t } = useTranslation('checkIn')
  const { popDialog, pushDialog } = useDialog()
  const { mutate: createInvoice, isLoading } = useCreateAddonInvoice()
  const { changeMembershipPlan } = useChangeMembershipPlan()
  const { data: enableMembershipPlanChange } = useSiteEnableMembershipPlanChange()
  const [selectedOption, setSelectedOption] = useState(null)

  const currentPrice = servicePrice ?? 0
  const currency = serviceCurrency ?? 'USD'

  const handleNext = () => {
    if (selectedOption === ADDON_OPTION.CHANGE_MEMBERSHIP) {
      if (!serviceId || !serviceName) {
        return
      }

      pushDialog(
        <ChangePlanDialog
          clientId={clientId}
          siteId={siteId}
          serviceId={serviceId}
          serviceName={serviceName}
          currentPrice={currentPrice}
          currency={currency}
          nextInvoiceDate={serviceNextBillingDate}
          siteSessionTypeId={siteSessionTypeId}
          onChangePlan={async (data) => {
            await changeMembershipPlan({
              siteId,
              serviceId,
              toServiceTemplateId: data.toServiceTemplateId,
              effectiveType: data.effectiveType,
              reason: data.reason,
              signedClientAgreementId: data.signedClientAgreementId,
            })
            popDialog()
          }}
        />
      )
      return
    }

    if (selectedOption === ADDON_OPTION.ONE_TIME_ADDON) {
      createInvoice(
        { siteId, clientId, equipmentId, siteSessionTypeId },
        {
          onSuccess: (data) => {
            const invoiceId = data?.createAddonInvoice?.invoiceId
            if (invoiceId) {
              window.open(
                `/s/${siteId}/${ROUTES.retailDashboard.children.checkout.path}?invoiceIds=${invoiceId}`,
                '_blank'
              )
            }
            popDialog()
          },
        }
      )
      return
    }

    popDialog()
  }

  const changeMembershipDisabled = !enableMembershipPlanChange

  return (
    <DialogLayout
      closeType={CLOSE_TYPES.TEXT}
      closeLabel={t('common:cancel')}
      closeAction={popDialog}
      size={SIZES.XS}
      primaryAction={{
        label: t('checkIn:equipments.addOn.next'),
        action: handleNext,
        props: { disabled: !selectedOption },
        isLoading,
      }}
    >
      <DialogContent sx={styles.dialogContent}>
        <Box sx={styles.iconShape}>
          <ExclamationTriangleIcon width={24} height={24} />
        </Box>

        <Box sx={styles.titleSection}>
          <Typography typography="header2" color="gray.80" align="center">
            {t('checkIn:equipments.addOn.membershipTitle')}
          </Typography>
          <Box sx={styles.descriptionRow}>
            <AvatarComponent
              item={{ src: clientProfileImage || '', alt: clientName }}
              size={AVATAR_SIZE.MEDIUM}
            />
            <Typography typography="medium" color="gray.64" align="center">
              {t('checkIn:equipments.addOn.membershipDescription', {
                name: clientName,
                sessionTypeName: sessionTypeName || t('checkIn:equipments.addOn.defaultName'),
              })}
            </Typography>
          </Box>
        </Box>

        <Box sx={styles.optionsContainer}>
          {serviceId && (
            <Tooltip
              title={
                changeMembershipDisabled
                  ? t('checkIn:equipments.addOn.changeMembershipDisabledTooltip')
                  : ''
              }
              placement="top"
            >
              <Box component="span" sx={styles.tooltipWrapper}>
                <Box
                  sx={[
                    styles.optionCard,
                    selectedOption === ADDON_OPTION.CHANGE_MEMBERSHIP && styles.optionCardSelected,
                    changeMembershipDisabled && { opacity: 0.5, cursor: 'not-allowed' },
                  ]}
                  onClick={
                    changeMembershipDisabled
                      ? undefined
                      : () => setSelectedOption(ADDON_OPTION.CHANGE_MEMBERSHIP)
                  }
                >
                  <Typography typography="header3" color="gray.100">
                    {t('checkIn:equipments.addOn.changeMembership')}
                  </Typography>
                  <Typography typography="medium" color="gray.100">
                    {t('checkIn:equipments.addOn.changeMembershipDescription')}
                  </Typography>
                </Box>
              </Box>
            </Tooltip>
          )}

          {showOneTimeAddon && (
            <Box
              sx={[
                styles.optionCard,
                selectedOption === ADDON_OPTION.ONE_TIME_ADDON && styles.optionCardSelected,
              ]}
              onClick={() => setSelectedOption(ADDON_OPTION.ONE_TIME_ADDON)}
            >
              <Typography typography="header3" color="gray.100">
                {t('checkIn:equipments.addOn.oneTimeAddon')}
              </Typography>
              <Typography typography="medium" color="gray.100">
                {t('checkIn:equipments.addOn.oneTimeAddonDescription')}
              </Typography>
              <Typography typography="medium" sx={styles.priceText}>
                {t('checkIn:equipments.addOn.oneTimeCharge', { price: addOnPrice })}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </DialogLayout>
  )
}

AddOnConfirmationDialog.propTypes = {
  addOnPrice: PropTypes.string,
  addOnName: PropTypes.string,
  sessionTypeName: PropTypes.string,
  clientName: PropTypes.string,
  clientProfileImage: PropTypes.string,
  addonConfigId: PropTypes.string,
  serviceId: PropTypes.string,
  serviceName: PropTypes.string,
  servicePrice: PropTypes.number,
  serviceCurrency: PropTypes.string,
  serviceNextBillingDate: PropTypes.string,
  siteId: PropTypes.string.isRequired,
  clientId: PropTypes.string.isRequired,
  equipmentId: PropTypes.string.isRequired,
  siteSessionTypeId: PropTypes.string,
  showOneTimeAddon: PropTypes.bool,
}

export default AddOnConfirmationDialog
